import api from '../api'
import { showWhitelistErrorModal } from './modals'
import { shouldPresentMediaService } from '../utils/mediaDeliveryUrls'

// http://plickers-api-documentation-pgjd3e8jb7.s3-website-us-east-1.amazonaws.com/#cloudinary

function debug(...args) {
  if (process.env.REACT_APP_ENV !== 'production') {
    console.log(...args)
  }
}

// TODO IMPORTANT !!! - setting this to true will use media service response for uploads
// in prod this should be enabled after full migration to media service as a final/final step
// originally intended for development testing
const USE_MEDIASERVICE_RESPONSE = false

export function uploadNewImage(file, thumbnail) {
  return (dispatch) => {
    const shouldUseMediaService = shouldPresentMediaService()

    const cloudinaryPromise = api.fetch('/cloudinary?type=image')
      .then((response) => dispatch(uploadToCloudinary(
        file,
        response.api_key,
        response.timestamp,
        response.signature,
        response.public_id,
        response.upload_preset,
      ))
        .then((response) => response)
        // upload image thumbnail if available (thumbnail comes from image search result)
        .catch(() => dispatch(uploadToCloudinary(
          thumbnail,
          response.api_key,
          response.timestamp,
          response.signature,
          response.public_id,
        ))
          .then((response) => response)
          .catch((error) => { // TODO better error handling
            if (error.response && (
              error.response.status === 400 ||
              error.response.status === 401 ||
              error.response.status === 404
            )) {
              throw error
            } else {
              dispatch(showWhitelistErrorModal('cloudinary'))
              throw error
            }
          })))

    if (shouldUseMediaService) {
      const mediaServicePromise = api.fetch('/mediaservice?type=image')
        .then((response) => dispatch(uploadToMediaService(
          file,
          response.timestamp,
          response.signature,
          response.public_id,
          response.upload_preset,
        ))
          .then((response) => response)
          // upload image thumbnail if available (thumbnail comes from image search result)
          .catch(() => dispatch(uploadToMediaService(
            thumbnail,
            response.timestamp,
            response.signature,
            response.public_id,
          ))
            .then((response) => response)
            .catch((error) => { // TODO better error handling
              if (error.response && (
                error.response.status === 400 ||
                error.response.status === 401 ||
                error.response.status === 404
              )) {
                throw error
              } else {
                dispatch(showWhitelistErrorModal('cloudinary'))
                throw error
              }
            })))

      return Promise.all([
        cloudinaryPromise,
        mediaServicePromise.catch(() => null),
      ])
        .then(([cloudinaryResponse, mediaServiceResponse]) => {
          if (USE_MEDIASERVICE_RESPONSE && mediaServiceResponse) {
            debug('using media service response', mediaServiceResponse)
            return mediaServiceResponse
          }
          debug('using cloudinary response', cloudinaryPromise)
          return cloudinaryResponse
        })
    }
    debug('using cloudinary response', cloudinaryPromise)
    return cloudinaryPromise
  }
}

export function uploadProfileImage(image) {
  return (dispatch) => {
    const shouldUseMediaService = shouldPresentMediaService()

    const cloudinaryPromise = api.fetch('/cloudinary?type=profile')
      .then((response) => dispatch(uploadToCloudinary(
        image,
        response.api_key,
        response.timestamp,
        response.signature,
        response.public_id,
        response.upload_preset,
      ))
        .then((response) => response)
        .catch(() => {}))

    if (shouldUseMediaService) {
      const mediaServicePromise = api.fetch('/mediaservice?type=profile')
        .then((response) => dispatch(uploadToMediaService(
          image,
          response.timestamp,
          response.signature,
          response.public_id,
          response.upload_preset,
        ))
          .then((response) => response)
          .catch(() => {}))

      return Promise.all([
        cloudinaryPromise,
        mediaServicePromise.catch(() => null),
      ])
        .then(([cloudinaryResponse, mediaServiceResponse]) => {
          if (USE_MEDIASERVICE_RESPONSE && mediaServiceResponse) {
            return mediaServiceResponse
          }
          return cloudinaryResponse
        })
    }

    return cloudinaryPromise
  }
}

export function uploadRepoImage(image, repoId) {
  return (dispatch) => {
    const shouldUseMediaService = shouldPresentMediaService()

    const cloudinaryPromise = api.fetch(`/cloudinary?type=repo&id=${repoId}`)
      .then((response) => dispatch(uploadToCloudinary(
        image,
        response.api_key,
        response.timestamp,
        response.signature,
        response.public_id,
        response.upload_preset,
      ))
        .then((response) => response)
        .catch(() => {}))

    if (shouldUseMediaService) {
      const mediaServicePromise = api.fetch(`/mediaservice?type=repo&id=${repoId}`)
        .then((response) => dispatch(uploadToMediaService(
          image,
          response.timestamp,
          response.signature,
          response.public_id,
          response.upload_preset,
        ))
          .then((response) => response)
          .catch(() => {}))

      return Promise.all([
        cloudinaryPromise,
        mediaServicePromise.catch(() => null),
      ])
        .then(([cloudinaryResponse, mediaServiceResponse]) => {
          if (USE_MEDIASERVICE_RESPONSE && mediaServiceResponse) {
            return mediaServiceResponse
          }
          return cloudinaryResponse
        })
    }

    return cloudinaryPromise
  }
}

// used for showing progress bar during sound upload
export function uploadSoundToCloudinaryWithProgress(file, onProgress) {
  return (dispatch) => {
    const shouldUseMediaService = shouldPresentMediaService({ isSound: true })

    const cloudinaryPromise = api.fetch('/cloudinary?type=sound')
      .then((response) => dispatch(uploadToCloudinaryWithProgress(
        file,
        // Only use cloudinary progress if not using mediaservice response
        shouldUseMediaService && USE_MEDIASERVICE_RESPONSE ? () => {} : onProgress,
        response.api_key,
        response.timestamp,
        response.signature,
        response.public_id,
        response.upload_preset,
      ))
        .then((uploadResponse) => uploadResponse))

    if (shouldUseMediaService) {
      const mediaServicePromise = api.fetch('/mediaservice?type=sound')
        .then((response) => dispatch(uploadToMediaServiceWithProgress(
          file,
          // Use mediaservice progress if USE_MEDIASERVICE_RESPONSE is true
          USE_MEDIASERVICE_RESPONSE ? onProgress : () => {},
          response.timestamp,
          response.signature,
          response.public_id,
          response.upload_preset,
        ))
          .then((uploadResponse) => uploadResponse))

      return Promise.all([
        cloudinaryPromise,
        mediaServicePromise.catch(() => null),
      ])
        .then(([cloudinaryResponse, mediaServiceResponse]) => {
          if (USE_MEDIASERVICE_RESPONSE && mediaServiceResponse) {
            debug('using media service response', mediaServiceResponse)
            return mediaServiceResponse
          }
          debug('using cloudinary response', cloudinaryResponse)
          return cloudinaryResponse
        })
    }
    debug('using cloudinary response', cloudinaryPromise)
    return cloudinaryPromise
  }
}

export function uploadToCloudinaryWithProgress(
  file,
  onProgress,
  apiKey,
  timestamp,
  signature,
  publicId,
  uploadPreset,
) {
  return () => api.postCloudinaryWithProgress(
    file,
    onProgress,
    apiKey,
    timestamp,
    signature,
    publicId,
    uploadPreset,
  )
    .then((uploadResponse) => uploadResponse)
}

function uploadToMediaServiceWithProgress(
  file,
  onProgress,
  timestamp,
  signature,
  publicId,
  uploadPreset,
) {
  return () => api.postMediaServiceWithProgress(
    file,
    onProgress,
    timestamp,
    signature,
    publicId,
    uploadPreset,
  )
    .then((uploadResponse) => uploadResponse)
}

function uploadToCloudinary(
  file,
  apiKey,
  timestamp,
  signature,
  publicId,
  uploadPreset,
) {
  const form = new FormData()
  form.append('file', file)
  form.append('api_key', apiKey)
  form.append('timestamp', timestamp)
  form.append('signature', signature)
  form.append('public_id', publicId)
  if (uploadPreset) {
    form.append('upload_preset', uploadPreset)
  }
  return () => api.postCloudinary(form)
    .then((response) => response)
}

function uploadToMediaService(
  file,
  timestamp,
  signature,
  publicId,
  uploadPreset,
) {
  const form = new FormData()
  form.append('file', file)
  form.append('timestamp', timestamp)
  form.append('signature', signature)
  form.append('public_id', publicId)
  if (uploadPreset) {
    form.append('upload_preset', uploadPreset)
  }
  return () => api.postMediaService(form)
    .then((response) => response)
}
